"use client";

import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import type { ActionRecord } from "@/services/action-service";
import { domId } from "@/lib/dom-id";
import { getModuleDisplayName, getRawModuleName } from "@/lib/module-display";
import { pushWithMinimumDelay } from "@/lib/navigation";
import { Button } from "@/components/ui";

interface ActionTableProps {
  actions: ActionRecord[];
  loading: boolean;
  isValidating?: boolean;
  onView: (action: ActionRecord) => void;
  onEdit: (action: ActionRecord) => void;
  onDelete: (action: ActionRecord) => void;
  formatDate: (date?: string | null) => string;
  sortBy?: string | '';
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  systemId?: string;
  systemName?: string;
  hideColumns?: ('lastUpdated' | 'actions')[];
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /**
   * Fill the remaining height of a flex-column parent instead of capping the
   * scroll frame at 620px, for a container where the table is the last item
   * (the Add Connector review step). The frame keeps 80px (the header and the
   * top of the first row), so on a short window the parent scrolls instead of
   * squeezing the table to nothing.
   */
  fillHeight?: boolean;
}

// Sort icon component
const SortIcon = ({ field, sortBy, sortOrder }: { 
  field: string; 
  sortBy: string | ''; 
  sortOrder?: 'asc' | 'desc' 
}) => {
  if (sortBy !== field) {
    return (
      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
      </svg>
    );
  }
  
  return sortOrder === 'desc' ? (
    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
};

function getMethodColor(method: string): string {
  const m = method.toUpperCase();
  if (m === "GET") return "text-blue-700 bg-blue-50";
  if (m === "POST") return "text-green-700 bg-green-50";
  if (m === "PUT") return "text-orange-700 bg-orange-50";
  if (m === "DELETE") return "text-red-700 bg-red-50";
  if (m === "PATCH") return "text-purple-700 bg-purple-50";
  return "text-slate-700 bg-slate-50";
}

function renderFormattedDescription(text: string): React.ReactNode {
  if (!text) {
    return <span className="text-sm text-slate-600">—</span>;
  }

  const truncatedText =
    text.length > 300 ? `${text.substring(0, 300)}...` : text;
  const html = truncatedText
    .replace(
      /`([^`]+)`/g,
      '<code class="bg-slate-100 px-1 py-0.5 rounded text-xs font-mono text-slate-800">$1</code>',
    )
    .replace(/\n/g, "<br>");

  return (
    <div
      className="text-sm text-slate-600 break-words leading-relaxed"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function ActionTable({
  actions,
  loading,
  isValidating = false,
  onView,
  onEdit,
  onDelete,
  formatDate,
  sortBy,
  sortOrder,
  onSort,
  systemId,
  systemName,
  hideColumns = [],
  canView = true,
  canEdit = true,
  canDelete = true,
  fillHeight = false,
}: ActionTableProps) {
  const t = useTranslations("ActionTable");
  const locale = useLocale();

  const showLastUpdated = !hideColumns.includes('lastUpdated');
  const showActions = !hideColumns.includes('actions') && (canEdit || canDelete);
  const displaySortBy = sortBy === 'category' ? 'module' : sortBy || '';
  const columnWidths = showLastUpdated
    ? showActions
      ? {
          action: 'w-[28%]',
          module: 'w-[16%]',
          method: 'w-[10%]',
          endpoint: 'w-[23%]',
          lastUpdated: 'w-[14%]',
          actions: 'w-[9%]',
        }
      : {
          action: 'w-[34%]',
          module: 'w-[18%]',
          method: 'w-[10%]',
          endpoint: 'w-[24%]',
          lastUpdated: 'w-[14%]',
        }
    : showActions
      ? {
          action: 'w-[36%]',
          module: 'w-[18%]',
          method: 'w-[10%]',
          endpoint: 'w-[27%]',
          actions: 'w-[9%]',
        }
      : {
          action: 'w-[42%]',
          module: 'w-[20%]',
          method: 'w-[11%]',
          endpoint: 'w-[27%]',
        };

  const router = useRouter();

  const getActionDetailsPath = (actionId: string) => {
    if (!systemId) return "";
    const params = new URLSearchParams();
    params.set("system_id", systemId);
    params.set("action_id", actionId);
    if (systemName) {
      params.set("name", systemName);
    }
    return `/${locale}/specs/action?${params.toString()}`;
  };
  
  const handleRowClick = (action: ActionRecord) => {
    if (!canView) return;
    if (systemId) {
      void pushWithMinimumDelay(router, getActionDetailsPath(action.action_id));
      return;
    }
    onView(action);
  };

  const handleRowHover = (action: ActionRecord) => {
    if (canView && systemId) {
      router.prefetch(getActionDetailsPath(action.action_id));
    }
  };
  if (loading && actions.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <div className="text-sm text-slate-500">{t("loading")}</div>
      </div>
    );
  }

  if (!loading && actions.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Image
            src="/emty_box.svg"
            alt={t("empty.alt")}
            width={128}
            height={128}
            className="w-32 h-32"
          />
          <div className="space-y-2">
            <p className="text-base font-medium text-slate-700">{t("empty.title")}</p>
            <p className="text-sm text-slate-500">{t("empty.subtitle")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${fillHeight ? 'min-h-[80px] flex-1' : 'max-h-[620px]'} overflow-auto overscroll-x-contain rounded-lg bg-white transition-opacity duration-200 ${isValidating ? 'opacity-60' : 'opacity-100'}`}>
      <div className="min-w-[1000px]">
        <table className="w-full min-w-[1000px] table-fixed">
          <colgroup>
            <col className={columnWidths.action} />
            <col className={columnWidths.module} />
            <col className={columnWidths.method} />
            <col className={columnWidths.endpoint} />
            {showLastUpdated && <col className={columnWidths.lastUpdated} />}
            {showActions && <col className={columnWidths.actions} />}
          </colgroup>
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <Button 
                  data-testid={domId("btn", "action-table-sort-name")}
                   variant="unstyled" className="flex items-center gap-2 transition-colors"
                  onClick={() => onSort?.('name')}
                >
                  {t("columns.apiAction")}
                  <SortIcon field="name" sortBy={displaySortBy} sortOrder={sortOrder} />
                </Button>
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <Button 
                  data-testid={domId("btn", "action-table-sort-module")}
                   variant="unstyled" className="flex items-center gap-2 transition-colors"
                  onClick={() => onSort?.('module')}
                >
                  {t("columns.module")}
                  <SortIcon field="module" sortBy={displaySortBy} sortOrder={sortOrder} />
                </Button>              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {t("columns.method")}
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {t("columns.endpoint")}
              </th>
              {showLastUpdated && (
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <Button
                    data-testid={domId("btn", "action-table-sort-last-updated")}
                     variant="unstyled" className="flex items-center gap-2 transition-colors"
                    onClick={() => onSort?.('last_updated_time')}
                  >
                    {t("columns.lastUpdated")}
                    <SortIcon field="last_updated_time" sortBy={displaySortBy} sortOrder={sortOrder || 'desc'} />
                  </Button>
                </th>
              )}
              {showActions && (
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {t("columns.action")}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {actions.map((action) => {
              const method = action.logic?.api_call?.method || "GET";
              const endpoint = action.logic?.api_call?.endpoint || "/";
              const rawModuleName = getRawModuleName(action.category);
              const category = getModuleDisplayName(action.category) || "—";

              return (
                <tr
                  key={action.action_id}
                  data-testid={domId("div", `action-table-action-${action.action_id}`)}
                  className={`${canView ? "cursor-pointer" : ""} transition-colors`}
                  onClick={() => handleRowClick(action)}
                  onMouseEnter={() => handleRowHover(action)}
                >
                  {/* API Action - Name & Description */}
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="break-words font-semibold text-slate-900 text-sm">
                        {action.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {renderFormattedDescription(action.description || "")}
                      </div>
                    </div>
                  </td>
                  
                  {/* Module */}
                  <td className="px-6 py-4">
                    <span
                      className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700"
                      title={rawModuleName || undefined}
                    >
                      <span className="block max-w-full truncate">{category}</span>
                    </span>
                  </td>
                  
                  {/* Method */}
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${getMethodColor(method)}`}
                    >
                      {method}
                    </span>
                  </td>
                  
                  {/* Endpoint */}
                  <td className="px-6 py-4">
                    <span className="block break-all font-mono text-sm text-slate-600">
                      {endpoint}
                    </span>
                  </td>
                  
                  {/* Last Updated */}
                  {showLastUpdated && (
                    <td className="px-6 py-4 w-32">
                      <span className="text-sm text-slate-500 whitespace-pre">
                        {formatDate(action.last_updated_time)}
                      </span>
                    </td>
                  )}
                  {/* Action buttons */}
                  {showActions && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {canEdit && <Button
                          data-testid={domId("btn", `action-table-edit-action-${action.action_id}`)}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (systemId) {
                              void pushWithMinimumDelay(
                                router,
                                getActionDetailsPath(action.action_id),
                              );
                            } else {
                              // Fallback to modal if no systemId
                              onEdit(action);
                            }
                          }}
                          className="p-1.5 text-slate-400 transition-colors"
                          title={t("actions.edit")}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Button>}
                        {canDelete && <Button
                          data-testid={domId("btn", `action-table-delete-action-${action.action_id}`)}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(action);
                          }}
                          className="p-1.5 text-slate-400 transition-colors"
                          title={t("actions.delete")}
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
